import type {
    FirebaseApp,
} from 'firebase/app';
import type {
    Firestore,
    DocumentData,
    DocumentSnapshot,
    QuerySnapshot,
    FirestoreError,
} from 'firebase/firestore';
import {
    getFirestore,
    doc,
    getDocFromServer,
    addDoc,  // auto-generated id
    setDoc,  // own id
    // updateDoc, // change only parts of doc (one property?)
    deleteDoc,
    collection,
    // getDocsFromServer,
    // serverTimestamp,
    // Bytes,
    // query as firebaseQuery,
    // where,
    onSnapshot,
} from 'firebase/firestore'
import type {User} from 'firebase/auth';
import type {ProVersionDocDB} from '$lib/classes/stores/pro-version.svelte';
import type {ReviewCountDoc} from '$shared/review-count-doc';
import type {SubscriptionDoc} from '$shared/subscription-doc';
import type {UserSettingsDoc} from '$lib/classes/stores/user-settings.svelte';
import {UserWordConverter} from '$lib/classes/data/user-word-converter';
import {UserWord} from '$lib/classes/data/user-word';
import {convertBetweenWordIdAndDbKey} from '$lib/utils/userWords/convertBetweenWordIdAndDbKey';
import type {SurveyRecord} from '$lib/classes/_types/survey';
import type {TestimonialRecord} from '$lib/classes/_types/testimonialRecord';
import type {MobileSubscriptionJson} from '$lib/classes/data/mobile-subscription-json';
import {Action} from '$lib/classes/_types/action';
import type {DocChange} from '$lib/classes/_types/doc-change';
import type {Database as IDatabase} from '$lib/classes/interfaces/database'

export class Database implements IDatabase {
    //NOTE CHANGE ALSO IN CLOUD FUNCTION .auth.user().onDelete() & Manage-Users !!!
    private readonly DB_COLLECTION_MAIN_USER_WORDS = 'user-words-v1' // docId = uid
    private readonly DB_COLLECTION_SUB_USER_WORDS = 'user-words' // docId = wordId
    private readonly DB_COLLECTION_USER_SETTINGS = 'user-stgs'
    private readonly DB_COLLECTION_PRO_VERSION = 'pro-version'
    private readonly DB_COLLECTION_SUBSCRIPTION = 'subscription'
    private readonly DB_COLLECTION_MOBILE_SUBSCRIPTION = 'mobile-subscription'
    private readonly DB_COLLECTION_REVIEW_COUNT = 'review-count'
    private readonly DB_COLLECTION_NEXT_FEATURE = 'next-feature'
    private readonly DB_COLLECTION_TESTIMONIAL = 'testimonial'

    private readonly db: Firestore

    constructor(
        firebaseApp: FirebaseApp,
        private readonly userWordConverter: UserWordConverter
    ) {
        this.db = getFirestore(firebaseApp)
    }

    private async getDoc(collection: string, docId: string) {
        const docRef = doc(this.db, collection, docId)
        const response = await getDocFromServer(docRef)
        if (response.exists()) {
            return response.data()
        } else {
            return null
        }
    }

    // auto-generated Id
    private async addDoc(path: string, docObj: DocumentData) {
        const collectionRef = collection(this.db, path)
        return await addDoc(collectionRef, docObj)
    }

    private async setDoc(collection: string, docId: string, docObj: DocumentData) {
        const docRef = doc(this.db, collection, docId)
        await setDoc(docRef, docObj)
    }

    //merge compared to update >> no error if doc doesn't exist, it's then created
    private async setDocMerge(collection: string, docId: string, docObj: DocumentData) {
        const docRef = doc(this.db, collection, docId)
        await setDoc(docRef, docObj, {merge: true})
    }

    private async deleteDoc(collection: string, docId: string) {
        await deleteDoc(doc(this.db, collection, docId));
    }

    private toDocOrNull(snapshot: DocumentSnapshot): DocumentData | null {
        if (snapshot.exists()) {
            return snapshot.data()
        } else {
            return null
        }
    }

    private subscribeDoc(
        collection: string,
        docId: string,
        callback: (doc: DocumentData | null) => void,
        options: {
            ignoreLocalWrites?: boolean,
            skipFirstValueInCallback?: boolean
        } = {}
    ): SubscriptionResult<DocumentData | null> {

        const {
            ignoreLocalWrites = false,
            skipFirstValueInCallback = false
        } = options

        let resolveFirstSnapshot: (doc: DocumentData | null) => void
        let rejectFirstSnapshot: (error: FirestoreError) => void
        let isFirstSnapshotResolved = false

        const firstValue = new Promise<DocumentData | null>((resolve, reject) => {
            resolveFirstSnapshot = resolve
            rejectFirstSnapshot = reject
        })

        const docRef = doc(this.db, collection, docId)

        const unsubscribe = onSnapshot(
            docRef,
            (snapshot) => {
                if (!isFirstSnapshotResolved) {
                    isFirstSnapshotResolved = true
                    resolveFirstSnapshot(this.toDocOrNull(snapshot))
                    if (skipFirstValueInCallback) return
                }
                if (snapshot.metadata.hasPendingWrites && ignoreLocalWrites) return
                callback(this.toDocOrNull(snapshot))
            },
            (error: FirestoreError) => {
                if (!isFirstSnapshotResolved) {
                    isFirstSnapshotResolved = true
                    rejectFirstSnapshot(error)
                    return
                }
                console.error('database.subscribeDoc - callback', error)
            })
        return {
            firstValue,
            unsubscribe,
        }
    }

    private toChanges(querySnapshot: QuerySnapshot): DocChange<DocumentData>[] {
        const docChanges = querySnapshot.docChanges()
        return docChanges.map(change => {
            const doc = change.doc.data()
            const type = change.type
            const action = type === 'added' ? Action.Added : type === 'modified' ? Action.Modified : Action.Removed
            const changedLocally = change.doc.metadata.hasPendingWrites
            return {
                data: doc,
                action,
                changedLocally
            }
        })
    }

    private subscribeCollection(
        collectionPath: string,
        callback: (docChanges: DocChange<DocumentData>[]) => void
    ): SubscriptionResult<DocChange<DocumentData>[]> {

        let resolveFirstSnapshot: (docChanges: DocChange<DocumentData>[]) => void
        let rejectFirstSnapshot: (error: FirestoreError) => void
        let isFirstSnapshotResolved = false

        const firstValue = new Promise<DocChange<DocumentData>[]>((resolve, reject) => {
            resolveFirstSnapshot = resolve
            rejectFirstSnapshot = reject
        })

        const q = collection(this.db, collectionPath)
        const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
                const changes = this.toChanges(querySnapshot)
                if (!isFirstSnapshotResolved) {
                    isFirstSnapshotResolved = true
                    resolveFirstSnapshot(changes)
                }
                callback(changes)
            },
            (error: FirestoreError) => {
                if (!isFirstSnapshotResolved) {
                    isFirstSnapshotResolved = true
                    rejectFirstSnapshot(error)
                    return
                }
                console.error('database.subscribeCollection - callback', error)
            })
        return {
            firstValue,
            unsubscribe,
        }
    }

    // PUBLIC API

    subscribeUserWordsCollectionNew(uid: User['uid'], callback: (docChanges: DocChange<DocumentData>[]) => void): SubscriptionResult<DocChange<DocumentData>[]> {
        const path = this.DB_COLLECTION_MAIN_USER_WORDS + '/' + uid + '/' + this.DB_COLLECTION_SUB_USER_WORDS
        return this.subscribeCollection(
            path,
            callback,
        )
    }

    //TODO UserWord -> Json conversion in UserWordManager
    async setUserWordsDoc(uid: User['uid'], userWord: UserWord) {
        const path = this.DB_COLLECTION_MAIN_USER_WORDS + '/' + uid + '/' + this.DB_COLLECTION_SUB_USER_WORDS
        const docId = convertBetweenWordIdAndDbKey(userWord.id)
        const docObj = this.userWordConverter.toJson(userWord)
        return await this.setDocMerge(path, docId, docObj)
    }

    async deleteUserWord(uid: User['uid'], userWord: UserWord) {
        const path = this.DB_COLLECTION_MAIN_USER_WORDS + '/' + uid + '/' + this.DB_COLLECTION_SUB_USER_WORDS
        const docId = convertBetweenWordIdAndDbKey(userWord.id)
        return await this.deleteDoc(path, docId)
    }

    async getUserSettingsDoc(uid: User['uid']) {
        return await this.getDoc(this.DB_COLLECTION_USER_SETTINGS, uid) as Partial<UserSettingsDoc> | null
    }

    async setUserSettingsDoc(uid: User['uid'], doc: Partial<UserSettingsDoc>) {
        await this.setDocMerge(this.DB_COLLECTION_USER_SETTINGS, uid, doc)
    }

    subscribeUserSettingsDoc(uid: User['uid'], callback: (doc: Partial<UserSettingsDoc> | null) => void): SubscriptionResult<Partial<UserSettingsDoc> | null> {
        return this.subscribeDoc(
            this.DB_COLLECTION_USER_SETTINGS,
            uid,
            (doc: DocumentData | null) => { callback(doc as Partial<UserSettingsDoc> | null) },
            {ignoreLocalWrites: true,}
        )
    }

    async getProVersionDoc(uid: string) {
        return await this.getDoc(this.DB_COLLECTION_PRO_VERSION, uid) as ProVersionDocDB | null
    }

    async setProVersionDoc(uid: User['uid'], doc: ProVersionDocDB) {
        await this.setDoc(this.DB_COLLECTION_PRO_VERSION, uid, doc)
    }

    subscribeProVersionDoc(uid: User['uid'], callback: (doc: ProVersionDocDB | null) => void) {
        return this.subscribeDoc(
            this.DB_COLLECTION_PRO_VERSION,
            uid,
            (doc: DocumentData | null) => { callback(doc as ProVersionDocDB | null) })
    }

    subscribeReviewCountDoc(uid: User['uid'], callback: (doc: ReviewCountDoc | null) => void) {
        return this.subscribeDoc(
            this.DB_COLLECTION_REVIEW_COUNT,
            uid,
            (doc: DocumentData | null) => callback(doc as ReviewCountDoc | null),
            {ignoreLocalWrites: true}
        )
    }

    async setReviewCountDoc(uid: User['uid'], doc: ReviewCountDoc) {
        await this.setDocMerge(this.DB_COLLECTION_REVIEW_COUNT, uid, doc)
    }

    subscribeSubscriptionDoc(uid: User['uid'], callback: (doc: SubscriptionDoc | null) => void) {
        return this.subscribeDoc(this.DB_COLLECTION_SUBSCRIPTION, uid, (doc: DocumentData | null) => {
            callback(doc as SubscriptionDoc | null)
        })
    }

    subscribeMobileSubscriptionDoc(uid: User['uid'], callback: (doc: MobileSubscriptionJson | null) => void) {
        return this.subscribeDoc(this.DB_COLLECTION_MOBILE_SUBSCRIPTION, uid, (doc: DocumentData | null) => {
            callback(doc as MobileSubscriptionJson | null)
        })
    }

    async getSurveyRecord(uid: User['uid']): Promise<SurveyRecord | null> {
        return await this.getDoc(this.DB_COLLECTION_NEXT_FEATURE, uid) as SurveyRecord | null
    }

    async setSurveyRecord(uid: User['uid'], doc: SurveyRecord): Promise<void> {
        await this.setDocMerge(this.DB_COLLECTION_NEXT_FEATURE, uid, doc)
    }

    async setTestimonial(doc: TestimonialRecord): Promise<void> {
        await this.addDoc(this.DB_COLLECTION_TESTIMONIAL, doc)
    }
}